import { useEffect, useId, useRef, useState } from 'react'
import { Popover } from './Popover'
import { cx } from '@/lib/cx'

interface Props {
  /** There is no visible label in a table cell; the column header is it. */
  label: string
  value: string
  onChange: (value: string) => void
  /**
   * The standard answers, offered first.
   *
   * Suggestions, not a closed list. Standardising what a prescription says is
   * the point — but a clinician who needs "alternate days for 2 weeks" must be
   * able to write it, and a dropdown that refuses would send them to scribble
   * it in the notes instead, where nothing can read it.
   */
  options: string[]
  placeholder?: string
  className?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/** A cell-sized combobox: the borderless input a table row wants, with the
 *  column's standard answers one tap away. */
export function CellCombobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
  onKeyDown,
}: Props) {
  const listId = `${useId()}-listbox`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  const matches = options.filter(
    (option) => !value.trim() || option.toLowerCase().includes(value.trim().toLowerCase()),
  )

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  function commit(option: string) {
    onChange(option)
    setOpen(false)
    setActive(-1)
    input.current?.focus()
  }

  function onKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) => Math.max(0, Math.min(matches.length - 1, i + step)))
      return
    }
    // Enter takes the highlighted suggestion; otherwise the table's own Enter
    // handling (next row) runs, which is what makes the grid fast.
    if (event.key === 'Enter' && open && active >= 0 && matches[active]) {
      event.preventDefault()
      commit(matches[active])
      return
    }
    onKeyDown?.(event)
  }

  return (
    <div className="relative" ref={wrap}>
      <input
        ref={input}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        className={className}
        onChange={(event) => {
          onChange(event.target.value)
          setOpen(true)
          setActive(-1)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />

      <Popover
        anchor={wrap}
        open={open && matches.length > 0}
        align="stretch"
        className="max-h-56 overflow-y-auto rounded-md border border-line bg-raised p-1 shadow-lg"
      >
        <ul
          id={listId}
          role="listbox"
          // Not the same name as the field: two elements answering to "Test 2"
          // makes the row ambiguous to a screen reader and to a test.
          aria-label={`${label} suggestions`}
          className="m-0 list-none p-0"
        >
          {matches.map((option, index) => (
            <li key={option} role="option" aria-selected={option === value}>
              <button
                type="button"
                // Pointer, not click: the cell keeps focus, so the row never
                // has to hand it back mid-prescription.
                onPointerDown={(event) => {
                  event.preventDefault()
                  commit(option)
                }}
                onPointerMove={() => setActive(index)}
                className={cx(
                  'flex min-h-touch w-full items-center rounded-sm px-3 text-sm',
                  index === active ? 'bg-hover text-fg' : 'text-fg-muted',
                  option === value && 'font-semibold text-fg',
                )}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  )
}
